import { ChangeEvent, JSX, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AdminMiniNok,
    AdminMiniNokConfiguration,
    ChosenNok,
    isAnswerEmpty,
    nokNeedsAnswering,
    noksMissingAnswers,
    useAdminMiniNoks,
    valueKeyFor,
} from '../hooks/admin-mini-noks';

export interface AdminMiniNokPanelProps {
    systemCode: string;
    /** The company whose details prefill the company-sourced points, or '' for none. */
    companyId: string;
    chosen: ChosenNok[];
    onChange: (chosen: ChosenNok[]) => void;
    disabled?: boolean;
}

const ADDRESS_FIELDS: { key: string, label: string }[] = [
    { key: 'line_one', label: 'Address line 1' },
    { key: 'line_two', label: 'Address line 2' },
    { key: 'city', label: 'City' },
    { key: 'county', label: 'County' },
    { key: 'postcode', label: 'Postcode' },
];

/**
 * Which Noks score this run, and the answers each of them needs.
 *
 * **A run with no Noks is scored against nothing.** Both Nok processors read their list from the
 * run's own configuration, so a run saved without one sends its mail, collects the reply, and
 * produces no grades at all - which is what every admin run did before 2026-09-11, silently,
 * because an empty list is a valid shape rather than an error. That is why this panel refuses to
 * be optional and why the composer will not activate without at least one.
 *
 * The three sources a value can come from are HQ-API's decision, not this panel's:
 *
 * - **company** held against the company, prefilled when one is picked, always overridable
 * - **recipient** answered per contact at send time, so only a fallback is collected here and an
 *   empty one never blocks anything
 * - **manual** nothing else can supply it, so the input is shown outright
 *
 * The customer portal renders the same three from the same payload. Keeping the decision in
 * `NokComposerCatalogue` rather than in either screen is what stops the two drifting.
 */
export const AdminMiniNokPanel: (props: AdminMiniNokPanelProps) => JSX.Element =
    (props: AdminMiniNokPanelProps): JSX.Element => {
        const { noks, loading, error } = useAdminMiniNoks(props.systemCode, props.companyId);
        const { chosen, onChange } = props;

        /**
         * Noks whose value the admin has actually typed into.
         *
         * **This is what lets a company be picked after the Noks are chosen.** The catalogue
         * reloads when the company changes, carrying fresh prefills for the four company-sourced
         * points - but a chosen Nok holds a *copy* of its configuration, so without this the
         * prefill never reached it and the field stayed blank. Copying it back unconditionally is
         * the opposite mistake: it would wipe whatever the admin had typed.
         *
         * So an untouched Nok follows the company and a touched one keeps its answer. Held in a
         * ref rather than state because nothing renders from it.
         */
        const touched = useRef<Set<number>>(new Set<number>());

        // The reload carries new prefills. Push them into the chosen Noks that have not been
        // edited, and leave the rest exactly as the admin left them.
        useEffect((): void => {
            if (noks.length === 0 || chosen.length === 0) {
                return;
            }

            let changed: boolean = false;
            const refreshed: ChosenNok[] = chosen.map((picked: ChosenNok): ChosenNok => {
                if (touched.current.has(picked.id)) {
                    return picked;
                }
                const loaded: AdminMiniNok | undefined = noks.find(
                    (candidate: AdminMiniNok): boolean => candidate.id === picked.id
                );
                if (loaded === undefined
                    || JSON.stringify(loaded.configuration) === JSON.stringify(picked.configuration)) {
                    return picked;
                }
                changed = true;

                return { ...picked, configuration: { ...loaded.configuration } };
            });

            if (changed) {
                onChange(refreshed);
            }
        }, [noks]);

        function isOn(nok: AdminMiniNok): boolean {
            return chosen.some((picked: ChosenNok): boolean => picked.id === nok.id);
        }

        function toggle(nok: AdminMiniNok): void {
            if (isOn(nok)) {
                // Switching off forgets the answer, so switching back on prefills again rather
                // than restoring something the admin removed on purpose.
                touched.current.delete(nok.id);
                onChange(chosen.filter((picked: ChosenNok): boolean => picked.id !== nok.id));

                return;
            }

            // The catalogue's own block is copied in, prefills and all, and the admin edits that
            // copy. Holding a reference would mean a reload for a company change rewriting answers
            // the admin had already typed.
            onChange([...chosen, { ...nok, configuration: { ...nok.configuration } }]);
        }

        function answer(nok: ChosenNok, value: unknown): void {
            // From here on this Nok's value is the admin's, and a company change must not take it.
            touched.current.add(nok.id);
            onChange(chosen.map((picked: ChosenNok): ChosenNok =>
                picked.id === nok.id
                    ? { ...picked, configuration: { ...picked.configuration, [valueKeyFor(picked)]: value } }
                    : picked));
        }

        const missing: ChosenNok[] = noksMissingAnswers(chosen);

        function fieldsFor(nok: ChosenNok): JSX.Element | null {
            const configuration: AdminMiniNokConfiguration = nok.configuration;
            const value: unknown = configuration[valueKeyFor(nok)];

            if (configuration.source === 'recipient') {
                return (
                    <Typography sx={ { fontSize: '12px', color: 'text.secondary' } }>
                        { 'Answered from each recipient when this goes out.' }
                    </Typography>
                );
            }

            if (configuration.type === 'address') {
                const address = (value ?? {}) as Record<string, string>;

                return (
                    <Stack spacing={ 1 }>
                        { ADDRESS_FIELDS.map((field): JSX.Element => (
                            <TextField key={ field.key }
                                       size="small"
                                       label={ field.label }
                                       value={ address[field.key] ?? '' }
                                       disabled={ props.disabled }
                                       onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                                           answer(nok, { ...address, [field.key]: e.target.value }) } />
                        )) }
                    </Stack>
                );
            }

            if (configuration.type === 'multiple') {
                const items: string[] = Array.isArray(value) ? value as string[] : [];

                return (
                    <TextField size="small"
                               fullWidth
                               multiline
                               minRows={ 2 }
                               label="Values"
                               placeholder="One per line"
                               value={ items.join('\n') }
                               disabled={ props.disabled }
                               helperText="One per line"
                               onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                                   answer(nok, e.target.value.split('\n')) } />
                );
            }

            if (configuration.type === 'single') {
                return (
                    <TextField size="small"
                               fullWidth
                               label="Value"
                               value={ typeof value === 'string' ? value : '' }
                               disabled={ props.disabled }
                               error={ configuration.required && isAnswerEmpty(value) }
                               helperText={ configuration.source === 'company'
                                   ? (configuration.prefilled
                                       ? 'Taken from the company. Change it if this run needs something else.'
                                       : 'No company picked, so type it here.')
                                   : undefined }
                               onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                                   answer(nok, e.target.value) } />
                );
            }

            return null;
        }

        return (
            <Stack spacing={ 2 } sx={ { width: '100%' } }>
                <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
                    <Typography sx={ {
                        fontSize: 'var(--wf-eyebrow)',
                        letterSpacing: 'var(--wf-track)',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontWeight: 700,
                    } }>
                        { `Scored against (${ chosen.length })` }
                    </Typography>
                    { loading && <CircularProgress size={ 14 } /> }
                </Box>

                { error !== '' && (
                    <Alert severity="error" sx={ { fontSize: '13px' } }>{ error }</Alert>
                ) }

                { !loading && error === '' && noks.length === 0 && (
                    <Alert severity="warning" sx={ { fontSize: '13px' } }>
                        { 'This mini has no active Noks, so nothing can score a reply to this run.' }
                    </Alert>
                ) }

                { chosen.length === 0 && noks.length > 0 && (
                    <Alert severity="info" sx={ { fontSize: '13px' } }>
                        { 'Choose at least one. A run with none sends its mail and is never scored.' }
                    </Alert>
                ) }

                { missing.length > 0 && (
                    <Alert severity="warning" sx={ { fontSize: '13px' } }>
                        { missing.length === 1
                            ? `${ missing[0].name } still needs a value.`
                            : `${ missing.length } of the chosen Noks still need a value.` }
                    </Alert>
                ) }

                <Stack spacing={ 1 }>
                    { noks.map((nok: AdminMiniNok): JSX.Element => {
                        const on: boolean = isOn(nok);
                        const picked: ChosenNok | undefined = chosen.find(
                            (candidate: ChosenNok): boolean => candidate.id === nok.id
                        );

                        return (
                            <Box key={ nok.id }
                                 sx={ {
                                     border: '1px solid',
                                     borderColor: on ? 'primary.main' : 'divider',
                                     borderRadius: '10px',
                                     padding: '10px 12px',
                                 } }>
                                <Box sx={ { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' } }>
                                    <FormControlLabel
                                        control={ <Checkbox size="small"
                                                            checked={ on }
                                                            disabled={ props.disabled }
                                                            onChange={ (): void => toggle(nok) } /> }
                                        label={
                                            <Tooltip title={ nok.description ?? '' }>
                                                <Typography sx={ { fontSize: '14px', fontWeight: 600 } }>
                                                    { nok.name }
                                                </Typography>
                                            </Tooltip>
                                        }
                                        sx={ { marginLeft: 0, alignItems: 'center' } }
                                    />
                                    { nok.tag !== '' && (
                                        <Chip size="small" variant="outlined" label={ nok.tag } sx={ { flexShrink: 0 } } />
                                    ) }
                                </Box>

                                { on && picked !== undefined && nokNeedsAnswering(picked) && (
                                    <Box sx={ { paddingLeft: '32px', paddingTop: '6px' } }>
                                        { fieldsFor(picked) }
                                    </Box>
                                ) }
                            </Box>
                        );
                    }) }
                </Stack>
            </Stack>
        );
    };
